import { useState, useEffect, useRef } from "react";
import { Building2, Mail, Phone, MapPin, Loader2, Upload } from "lucide-react";
import { industryOptions } from "../../data/companyData";
import {
  getOrganization,
  uploadOrganizationLogo
} from "../../services/organizationService";
import { useOrganizationLogo } from "../../hooks/useOrganizationLogo";
import { LoadingState, ErrorState } from "../ui/LoadingStates";

export default function CompanyProfileTab({ onSave, onCancel }) {
  const [companyInfo, setCompanyInfo] = useState({
    companyName: "",
    companyEmail: "",
    phoneNumber: "",
    industry: "",
    address: "",
    logoUrl: ""
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Use custom hook for logo fetching
  const { logoUrl, refreshLogo } = useOrganizationLogo();

  // Fetch organization data on mount
  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getOrganization();

        if (response.success && response.data) {
          // Map API response to component state
          setCompanyInfo({
            companyName: response.data.company_name || "",
            companyEmail: response.data.company_email || "",
            phoneNumber: response.data.phone_number || "",
            industry: response.data.industry || "",
            address: response.data.address || "",
            logoUrl: response.data.logo_url || ""
          });

          // Fetch logo if available
          if (response.data.logo_url) {
            await refreshLogo();
          }
        } else {
          setError(response.message || "Failed to load organization details");
        }
      } catch (err) {
        console.error("Error loading organization:", err);
        setError("An error occurred while loading organization details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const handleChange = (field, value) => {
    setCompanyInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Call parent's onSave with updated data
    onSave(companyInfo);
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setUploadError("Please upload a PNG, JPEG, or WEBP image");
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setUploadError("File size must be less than 2MB");
      return;
    }

    // Upload logo
    setUploading(true);
    setUploadError(null);

    try {
      const response = await uploadOrganizationLogo(file);

      if (response.success) {
        // Wait a moment for the server to process the image
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Fetch the newly uploaded logo
        await refreshLogo();

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Clear any previous errors
        setUploadError(null);
      } else {
        setUploadError(response.message || "Failed to upload logo");
      }
    } catch (err) {
      console.error("Logo upload error:", err);
      setUploadError("An error occurred while uploading logo");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <LoadingState message="Loading organization details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <ErrorState message={error} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Company Information
        </h3>
        <p className="text-sm text-gray-600">
          Update your company details and contact information
        </p>
      </div>

      {/* Logo Upload */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden bg-gray-50 border border-gray-200">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Company Logo"
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                // Fallback to icon if image fails to load
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <Building2
            className="w-10 h-10 text-gray-400"
            style={{
              display: logoUrl ? "none" : "flex"
            }}
          />
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleLogoClick}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload Logo
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 mt-1">
            PNG, JPEG, or WEBP. Max file size: 2MB
          </p>
          {uploadError && (
            <p className="text-xs text-red-600 mt-1">{uploadError}</p>
          )}
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        {/* Company Name & Email Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={companyInfo.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={companyInfo.companyEmail}
                onChange={(e) => handleChange("companyEmail", e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Phone & Industry Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                value={companyInfo.phoneNumber}
                onChange={(e) => handleChange("phoneNumber", e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Industry
            </label>
            <select
              value={companyInfo.industry}
              onChange={(e) => handleChange("industry", e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {industryOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={companyInfo.address}
              onChange={(e) => handleChange("address", e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 my-6"></div>

      {/* Footer Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}
